/**
 * Resident "Apply for a Permit" intake (Permits & Licensing, roadmap module 6 — MVP: apply → review
 * → approve/deny → inspections → issue → close; Permits FIDELITY Phase A adds per-type guided forms
 * on top of that shipped shell — docs/features/permits-fidelity.md, docs/design/
 * permits-fidelity-ui.md). Shell (permit-type select, address via LocationField, document upload,
 * CAPTCHA, error summary, tracking-code confirmation) is unchanged from the shipped MVP; once a type
 * with a `FormSchema` is chosen, `SchemaFormRenderer` mounts and renders that type's sections
 * dynamically — the SAME renderer drives all six real Linn Valley types (Building's ~40-field matrix
 * is just the biggest schema, not a special case). A permit type with no schema (legacy/free-text)
 * falls back to the original free-text project-description textarea.
 *
 * A permit application is NEVER anonymous, so — UNLIKE concern/KORA intake — this page does NOT
 * render the optional/anonymous `ContactSection` ("Want updates?"). The applicant's contact is the
 * REQUIRED Universal "Applicant & Contractor" schema fields (owner_name / owner_email / owner_phone),
 * rendered by `SchemaFormRenderer`; the server derives the notify contact from those owner
 * FormValues (handler.createPermit). We format-check owner_email client-side before submit too.
 *
 * GC reg#/Self/affidavit is Phase A only: a plain TEXT field + a "Self" homeowner-exemption checkbox
 * + an affidavit-ack — NO live registry validation call (that's Phase B, deferred). Required-docs
 * checklist warns, never blocks, except the two affidavit-ack items. Fee block shows the $35
 * application fee due now, the impact fee computed live from the tank-count answer, and "TBD" for
 * the total (set by staff at approval) — "pay at City Hall," never implying online payment exists.
 */
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { submitPermit, ApiError } from "../api/index.js";
import type { FormValues, RequiredDoc } from "../api/types.js";
import { useGeolocation } from "../components/useGeolocation.js";
import { LocationField } from "../components/LocationField.js";
import { AttachmentUploader, type AttachmentTile } from "../components/AttachmentUploader.js";
import { CaptchaField } from "../components/CaptchaField.js";
import { useCaptcha } from "../components/useCaptcha.js";
import { Field } from "../components/Field.js";
import { Button } from "../components/Button.js";
import { ErrorSummary, type FieldErrorRef } from "../components/ErrorSummary.js";
import { SpinnerIcon } from "../components/icons.js";
import { TownHeader } from "../components/TownHeader.js";
import { ResidentThemeRoot } from "../components/ResidentThemeRoot.js";
import { useTheme } from "../theme/ThemeProvider.js";
import { useDocumentTitle, pageTitle } from "../components/useDocumentTitle.js";
import { useEmbedClose } from "../components/useEmbedClose.js";
import { SchemaFormRenderer } from "../components/SchemaFormRenderer.js";
import { RequiredDocsChecklist } from "../components/RequiredDocsChecklist.js";
import { PermitFeeBlock } from "../components/PermitFeeBlock.js";
import { TANK_COUNT_FORM_KEY } from "../api/permitTypes.js";

const MAX_PROJECT_DESCRIPTION_LEN = 2000;
// Display-only defaults (permits-fidelity.md §A.5) — the SERVER stamps the authoritative app fee /
// derives the impact fee at submit from TenantSettings; these mirror shared.DefaultPermitAppFee /
// shared.DefaultPermitImpactFeePerTank so the pre-submit fee block reads correctly out of the box.
const DEFAULT_PERMIT_APP_FEE = 35;
const DEFAULT_PERMIT_IMPACT_FEE_PER_TANK = 500;

// The applicant's contact is collected as the schema's required Universal
// "Applicant & Contractor" fields (SchemaFormRenderer). owner_email is the
// notify address, so we format-check it before submit: the error is keyed
// `formValues.owner_email` in fieldErrors (SchemaFormRenderer slices the
// `formValues.` prefix to match a field), and its focus/scroll target is
// SchemaFormRenderer's DOM id `sf-owner_email`.
const OWNER_EMAIL_FORM_KEY = "owner_email";
const OWNER_EMAIL_ERROR_KEY = `formValues.${OWNER_EMAIL_FORM_KEY}`;
const OWNER_EMAIL_TARGET_ID = `sf-${OWNER_EMAIL_FORM_KEY}`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ID = {
  permitType: "field-permit-type",
  description: "field-permit-description",
  address: "field-permit-address",
  captcha: "field-permit-captcha",
};

interface ApplyForPermitPageProps {
  embed?: boolean;
}

export function ApplyForPermitPage({ embed = false }: ApplyForPermitPageProps): JSX.Element {
  const navigate = useNavigate();
  const { config, permitTypes } = useTheme();
  useDocumentTitle(pageTitle("Apply for a Permit", config.townName));
  useEmbedClose(embed);

  const [permitType, setPermitType] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const geo = useGeolocation({ onAddress: setAddress });
  const [tiles, setTiles] = useState<AttachmentTile[]>([]);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [affidavitAcks, setAffidavitAcks] = useState<Record<string, boolean>>({});
  const captcha = useCaptcha();
  const attachmentUploaderRef = useRef<HTMLDivElement>(null);

  const [summaryErrors, setSummaryErrors] = useState<FieldErrorRef[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "error">("idle");
  const [submitErrorMsg, setSubmitErrorMsg] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const selectedType = useMemo(
    () => permitTypes.find((t) => t.id === permitType) ?? null,
    [permitTypes, permitType],
  );
  const schema = selectedType?.formSchema;
  const requiredDocs: RequiredDoc[] = selectedType?.requiredDocs ?? [];

  const onPermitTypeChange = (nextId: string) => {
    const next = nextId || null;
    if (next !== permitType && (Object.keys(formValues).length || Object.values(affidavitAcks).some(Boolean))) {
      const ok = window.confirm(
        "Changing the permit type clears the application fields you've already filled in. Continue?",
      );
      if (!ok) return;
    }
    setPermitType(next);
    setFormValues({});
    setAffidavitAcks({});
  };

  // Missing REQUIRED affidavit-ack items block submit (ledger §A.4 exception — every other
  // required-docs item only warns).
  const missingAffidavitAcks = requiredDocs.filter((d) => d.kind === "affidavit-ack" && !affidavitAcks[d.key]);

  const tankCount = schema
    ? Number.parseInt(formValues[TANK_COUNT_FORM_KEY] ?? "", 10) || 0
    : 0;
  const hasTankField = Boolean(
    schema?.sections.some((s) => s.fields.some((f) => f.key === TANK_COUNT_FORM_KEY)),
  );

  const requiredMissing =
    !permitType ||
    (!schema && !description.trim()) ||
    !address.trim() ||
    missingAffidavitAcks.length > 0 ||
    !captcha.passed;

  const helperForMissing = useMemo(() => {
    const missing: string[] = [];
    if (!permitType) missing.push("a permit type");
    if (!schema && !description.trim()) missing.push("a project description");
    if (!address.trim()) missing.push("the project address");
    if (missingAffidavitAcks.length) missing.push("the required affidavit acknowledgment(s)");
    if (!captcha.passed) missing.push("the spam check");
    return missing;
  }, [permitType, schema, description, address, missingAffidavitAcks.length, captcha.passed]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitState === "submitting") return;

    const nextField: Record<string, string> = {};
    const summary: FieldErrorRef[] = [];
    if (!permitType) {
      nextField[ID.permitType] = "Choose a permit type.";
      summary.push({ targetId: ID.permitType, message: "Choose a permit type." });
    }
    if (!schema && !description.trim()) {
      nextField[ID.description] = "Describe the project.";
      summary.push({ targetId: ID.description, message: "Describe the project." });
    }
    if (!address.trim()) {
      nextField[ID.address] = "Enter the project address.";
      summary.push({ targetId: ID.address, message: "Enter the project address." });
    }
    // owner_email is the applicant's required notify address (a permit is never
    // anonymous). Required-presence is enforced by SchemaFormRenderer/server
    // ValidateFormValues; here we additionally format-check it so a malformed
    // notify address is rejected up front with a field-level error.
    const ownerEmail = (formValues[OWNER_EMAIL_FORM_KEY] ?? "").trim();
    if (schema && ownerEmail && !EMAIL_RE.test(ownerEmail)) {
      nextField[OWNER_EMAIL_ERROR_KEY] = "Enter a valid owner email — this is where we send permit updates.";
      summary.push({ targetId: OWNER_EMAIL_TARGET_ID, message: "Enter a valid owner email — this is where we send permit updates." });
    }
    for (const doc of missingAffidavitAcks) {
      const fid = `ack-${doc.key}`;
      nextField[fid] = `Please acknowledge: ${doc.label}.`;
      summary.push({ targetId: fid, message: `Please acknowledge: ${doc.label}.` });
    }
    if (!captcha.passed) {
      nextField[ID.captcha] = "Please complete the spam check.";
      summary.push({ targetId: ID.captcha, message: "Please complete the spam check." });
    }
    if (Object.keys(nextField).length) {
      setFieldErrors(nextField);
      setSummaryErrors(summary);
      return;
    }
    setFieldErrors({});
    setSummaryErrors([]);

    // Submit safety net: reuse the fresh inline-solved token (fast no-op) or re-challenge if expired.
    if (!(await captcha.ensureForSubmit())) {
      const msg = "We couldn't verify the spam check. Please try the puzzle again.";
      setSummaryErrors([{ targetId: ID.captcha, message: msg }]);
      return;
    }

    setSubmitState("submitting");
    setSubmitErrorMsg(null);
    try {
      // A permit is never anonymous: the applicant contact is the required
      // owner_* schema fields, and the server derives the notify ReporterContact
      // from them (handler.createPermit). The client no longer sends a separate
      // concern-style applicantContact — owner_email is the confirmation address.
      const ownerContactEmail = (formValues[OWNER_EMAIL_FORM_KEY] ?? "").trim() || undefined;
      // Fold the affidavit-ack checkbox states into formValues under their own RequiredDoc key so
      // the server's ValidateFormValues has nothing extra to reconcile — Phase A's schemas already
      // define the SAME keys as regular checkbox fields (e.g. self_contractor_affidavit_ack), so
      // this is a no-op merge when the schema already carries the field; harmless otherwise.
      const mergedFormValues: FormValues = { ...formValues };
      for (const [key, checked] of Object.entries(affidavitAcks)) {
        mergedFormValues[key] = checked ? "true" : "false";
      }
      const result = await submitPermit({
        permitType: permitType ?? "",
        projectDescription: schema
          ? description.trim() || selectedType?.label || "Permit application"
          : description.trim(),
        location: {
          address: address.trim(),
          ...(geo.result ? { lat: geo.result.lat, lng: geo.result.lng } : {}),
        },
        attachments: tiles.filter((t) => t.status === "done" && t.key).map((t) => t.key as string),
        ...(schema ? { formValues: mergedFormValues } : {}),
      });
      const permitTypeLabel = selectedType?.label ?? permitType ?? undefined;
      // Preserve `?embed=1&chrome=off` (and any other query params) across the navigate — App.tsx
      // derives `embed`/`chromeless` fresh from the URL on every route, so an embed submit that
      // dropped the search string here would silently lose chromeless/resize on /confirmation.
      navigate(`/confirmation${window.location.search}`, {
        state: {
          trackingCode: result.trackingCode,
          trackingUrl: result.trackingUrl,
          hasContact: Boolean(ownerContactEmail),
          email: ownerContactEmail,
          category: permitTypeLabel,
          isPermit: true,
        },
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Something went wrong submitting your permit application. Please try again.";
      setSubmitState("error");
      setSubmitErrorMsg(message);
    }
  };

  const submitLabel = submitState === "submitting" ? "Submitting…" : "Submit application";

  return (
    <ResidentThemeRoot className={`td-intake${embed ? " td-intake--embed" : ""}`}>
      <a href="#td-permit-form" className="td-skip-link">
        Skip to the permit application form
      </a>
      <TownHeader
        embed={embed}
        subcopy={`Apply for a building or business permit from ${config.townName}.`}
      />

      <main className="td-intake__main">
        <h1 className="td-intake__title">Apply for a Permit</h1>
        <p className="td-intake__lede">
          Choose the type of permit you need. The form below will guide you through exactly what{" "}
          {config.townName} requires for that project.
        </p>

        <ErrorSummary errors={summaryErrors} />

        {submitState === "error" && submitErrorMsg ? (
          <div className="td-alert td-alert--danger" role="alert">
            <p>{submitErrorMsg}</p>
            <Button variant="secondary" onClick={() => formRef.current?.requestSubmit()}>
              Try again
            </Button>
          </div>
        ) : null}

        <form
          id="td-permit-form"
          ref={formRef}
          className="td-form"
          noValidate
          onSubmit={onSubmit}
          aria-busy={submitState === "submitting"}
        >
          <Field id={ID.permitType} label="Permit type" required error={fieldErrors[ID.permitType]}>
            {(aria) => (
              <select
                {...aria}
                className="td-select"
                value={permitType ?? ""}
                onChange={(e) => onPermitTypeChange(e.target.value)}
              >
                <option value="">Choose a permit type…</option>
                {permitTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            )}
          </Field>

          {schema ? (
            <SchemaFormRenderer
              schema={schema}
              values={formValues}
              onChange={setFormValues}
              fieldErrors={Object.fromEntries(
                Object.entries(fieldErrors)
                  .filter(([k]) => k.startsWith("formValues."))
                  .map(([k, v]) => [k.slice("formValues.".length), v]),
              )}
            />
          ) : permitType ? (
            <Field
              id={ID.description}
              label="Project description"
              required
              helper="What are you building, adding, or changing?"
              error={fieldErrors[ID.description]}
            >
              {(aria) => (
                <>
                  <textarea
                    {...aria}
                    className="td-input td-textarea"
                    rows={4}
                    maxLength={MAX_PROJECT_DESCRIPTION_LEN}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g. 10x12 detached storage shed in the back yard."
                  />
                  {description.length > 400 ? (
                    <span className="td-charcount" aria-live="polite">
                      {description.length} / {MAX_PROJECT_DESCRIPTION_LEN}
                    </span>
                  ) : null}
                </>
              )}
            </Field>
          ) : null}

          <LocationField
            fieldId={ID.address}
            address={address}
            onAddressChange={setAddress}
            addressError={fieldErrors[ID.address]}
            geo={geo}
          />

          <div ref={attachmentUploaderRef}>
            <AttachmentUploader tiles={tiles} onChange={setTiles} />
          </div>

          {requiredDocs.length ? (
            <fieldset className="td-fieldset">
              <legend className="td-fieldset__legend">Required documents</legend>
              <RequiredDocsChecklist
                docs={requiredDocs}
                uploadedCount={tiles.filter((t) => t.status === "done").length}
                acks={affidavitAcks}
                onAckChange={(key, checked) =>
                  setAffidavitAcks((prev) => ({ ...prev, [key]: checked }))
                }
                onUploadClick={() =>
                  attachmentUploaderRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
                }
              />
            </fieldset>
          ) : null}

          {schema ? (
            <PermitFeeBlock
              appFee={selectedType?.permitAppFeeOverride ?? DEFAULT_PERMIT_APP_FEE}
              tankCount={hasTankField ? tankCount : undefined}
              impactFeePerTank={DEFAULT_PERMIT_IMPACT_FEE_PER_TANK}
            />
          ) : null}

          <CaptchaField
            ref={captcha.containerRef}
            id={ID.captcha}
            status={captcha.status}
            error={captcha.error ?? undefined}
            passed={captcha.passed}
            onChange={(v) => {
              captcha.onChange(v);
              if (v && fieldErrors[ID.captcha]) {
                setFieldErrors((f) => {
                  const { [ID.captcha]: _drop, ...rest } = f;
                  return rest;
                });
              }
            }}
            onRetry={captcha.retry}
          />

          <div className="td-submit">
            <Button
              type="submit"
              fullWidth
              disabled={submitState === "submitting"}
              aria-disabled={requiredMissing || submitState === "submitting"}
            >
              {submitState === "submitting" ? (
                <>
                  <SpinnerIcon width={18} height={18} />
                  {submitLabel}
                </>
              ) : (
                submitLabel
              )}
            </Button>
            {requiredMissing && submitState !== "submitting" ? (
              <p className="td-submit__hint" aria-live="polite">
                Still needed: {helperForMissing.join(", ")}.
              </p>
            ) : null}
          </div>
        </form>
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
        {config.privacyUrl ? (
          <a href={config.privacyUrl} rel="noopener">
            Privacy
          </a>
        ) : null}
      </footer>
    </ResidentThemeRoot>
  );
}
